/**
 * Firebase Analytics Provider
 * Handles Firebase Analytics event tracking
 * Uses ad-blocker-resistant naming
 */

import { analytics } from '../../firebase';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { createDebugLogger } from '../../../utils/debugLogger';

const logger = createDebugLogger('firebase');

/**
 * Firebase Analytics event mapping
 * Maps standard event names to Firebase Analytics events
 * Firebase Analytics has predefined events, so we'll use those where possible
 */
const FIREBASE_EVENT_MAP: Record<string, string> = {
    signup_started: 'begin_checkout',
    signup_step_viewed: 'view_item',
    signup_step_completed: 'add_to_cart',
    product_selected: 'add_to_cart',
    payment_method_created: 'add_payment_info',
    signup_completed: 'sign_up',
    signup_failed: 'exception',
    conversion_completed: 'purchase',
    payment_method_failed: 'add_payment_info',
    error_occurred: 'exception',
    button_clicked: 'select_content',
    form_field_completed: 'form_start',
};

/**
 * Track event with Firebase Analytics
 */
export const trackFirebaseEvent = (
    eventName: string,
    properties: Record<string, any> = {}
): void => {
    logger.track(eventName, properties);
    if (!analytics) {
        logger.warn('Firebase analytics not initialized');
        return;
    }

    try {
        // Map standard event name to Firebase event
        const firebaseEvent = FIREBASE_EVENT_MAP[eventName] || eventName;

        // Transform properties for Firebase Analytics
        const firebaseProperties: Record<string, any> = {};

        // Map common properties
        if (properties.value !== undefined) {
            firebaseProperties.value = properties.value;
        }
        if (properties.currency) {
            firebaseProperties.currency = properties.currency;
        } else {
            firebaseProperties.currency = 'USD';
        }

        // Map product/membership properties
        if (properties.membership_tier || properties.product_name) {
            firebaseProperties.item_name = properties.membership_tier || properties.product_name;
        }
        if (properties.price_id || properties.product_id) {
            firebaseProperties.item_id = properties.price_id || properties.product_id;
        }
        if (properties.product_name) {
            firebaseProperties.item_category = properties.product_name;
        }

        // Map step properties
        if (properties.step !== undefined) {
            firebaseProperties.content_type = `step_${properties.step}`;
        }

        // Map error properties
        if (eventName === 'error_occurred') {
            firebaseProperties.description = properties.error_message || 'Unknown error';
            firebaseProperties.fatal = properties.error_type === 'react_error_boundary' ? 1 : 0;
        }

        // Map transaction properties for purchase events (Firebase ecommerce requirements)
        if (firebaseEvent === 'purchase') {
            // Required: transaction_id, value, currency
            firebaseProperties.transaction_id = properties.transaction_id || properties.price_id || `txn_${Date.now()}`;
            firebaseProperties.value = properties.value !== undefined ? properties.value : 0;
            firebaseProperties.currency = properties.currency || 'USD';
            
            // Items array for ecommerce (Firebase REQUIRES items array for purchase events)
            // Always ensure items array is populated with complete product data
            if (properties.items && Array.isArray(properties.items) && properties.items.length > 0) {
                firebaseProperties.items = properties.items.map((item: any) => ({
                    item_id: item.item_id || item.id || properties.product_id || properties.price_id || '',
                    item_name: item.item_name || item.name || properties.membership_tier || properties.product_name || '',
                    item_category: item.item_category || item.category || 'membership',
                    price: item.price !== undefined && item.price !== null ? Number(item.price) : (properties.value || 0),
                    quantity: item.quantity !== undefined && item.quantity !== null ? Number(item.quantity) : 1,
                })).filter((item: any) => item.item_id && item.item_name); // Filter out invalid items
            } else {
                // Fallback: create items array from available data
                // This should rarely happen if processPurchaseEvent is called correctly
                firebaseProperties.items = [{
                    item_id: properties.product_id || properties.price_id || '',
                    item_name: properties.membership_tier || properties.product_name || 'Membership',
                    item_category: 'membership',
                    price: properties.value || 0,
                    quantity: 1,
                }];
            }
            
            // Ensure items array is never empty (Firebase requirement)
            if (!firebaseProperties.items || firebaseProperties.items.length === 0) {
                firebaseProperties.items = [{
                    item_id: properties.product_id || properties.price_id || 'unknown',
                    item_name: properties.membership_tier || properties.product_name || 'Membership',
                    item_category: 'membership',
                    price: properties.value || 0,
                    quantity: 1,
                }];
            }
            
            // Additional ecommerce parameters
            if (properties.billing_cycle) {
                firebaseProperties.billing_cycle = properties.billing_cycle;
            }
            if (properties.coupon_id) {
                firebaseProperties.coupon = properties.coupon_id;
            }
            if (properties.discount_amount_cents !== undefined && properties.discount_amount_cents !== null) {
                firebaseProperties.discount = properties.discount_amount_cents / 100;
            }
        }

        // Add custom parameters (Firebase Analytics supports custom parameters)
        Object.keys(properties).forEach((key) => {
            if (!firebaseProperties[key] && !key.startsWith('_') && key !== 'items') {
                // Only add if not already mapped and not a private property
                // Exclude 'items' as it's already processed above
                firebaseProperties[key] = properties[key];
            }
        });

        // Log the event
        logEvent(analytics, firebaseEvent, firebaseProperties);
    } catch (error) {
        console.warn('Firebase Analytics tracking failed:', error);
    }
};

/**
 * Set user ID for Firebase Analytics
 */
export const setFirebaseUserId = (userId: string): void => {
    if (!analytics) {
        return;
    }

    try {
        setUserId(analytics, userId);
    } catch (error) {
        console.warn('Firebase Analytics setUserId failed:', error);
    }
};

/**
 * Set user properties for Firebase Analytics
 */
export const setFirebaseUserProperties = (properties: Record<string, any>): void => {
    if (!analytics) {
        return;
    }

    try {
        setUserProperties(analytics, properties);
    } catch (error) {
        console.warn('Firebase Analytics setUserProperties failed:', error);
    }
};

/**
 * Track page view with Firebase Analytics
 */
export const trackFirebasePageView = (name: string, properties: Record<string, any> = {}): void => {
    if (!analytics) {
        return;
    }

    try {
        logEvent(analytics, 'page_view', {
            page_title: name,
            page_location: typeof window !== 'undefined' ? window.location.href : '',
            ...properties,
        });
    } catch (error) {
        console.warn('Firebase Analytics page view failed:', error);
    }
};

/**
 * Create Firebase Analytics plugin for analytics library
 */
export const createFirebaseAnalyticsPlugin = () => {
    return {
        name: 'firebase-analytics',
        config: {},
        initialize: () => {
            // Firebase Analytics is already initialized in firebase.ts
            // No additional initialization needed
        },
        track: ({ payload }: any) => {
            trackFirebaseEvent(payload.event, payload.properties || {});
        },
        page: ({ payload }: any) => {
            trackFirebasePageView(payload.name || 'Unknown', payload.properties || {});
        },
        identify: ({ userId, traits }: any) => {
            if (userId) {
                setFirebaseUserId(userId);
            }
            if (traits && Object.keys(traits).length > 0) {
                setFirebaseUserProperties(traits);
            }
        },
    };
};

